import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, CircleCheck, CircleDollarSign, FileText, History,
  Loader2, Pencil, ReceiptText, Search, TriangleAlert, UserRound, Wallet,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { KpiCard } from "@/components/common/KpiCard";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/constants";

const BUCKETS = [
  { value: "all", label: "All records" },
  { value: "invoice_pending", label: "Invoice Pending" },
  { value: "payment_pending", label: "Payment Pending" },
  { value: "payment_received", label: "Payment Received" },
];

const bucketOf = (b) => (b.payment_received ? "payment_received" : b.invoice_raised ? "payment_pending" : "invoice_pending");

const BillingBadge = ({ record }) => {
  const bucket = bucketOf(record);
  const map = {
    invoice_pending: ["Invoice Pending", "bg-amber-50 text-amber-800 border-amber-300"],
    payment_pending: ["Payment Pending", "bg-blue-50 text-blue-700 border-blue-200"],
    payment_received: ["Payment Received", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  };
  const [label, cls] = map[bucket];
  return <Badge variant="outline" className={`rounded-full font-medium whitespace-nowrap ${cls}`} data-testid="billing-bucket-badge">{label}</Badge>;
};

const EVENT_LABELS = {
  BILLING_CREATED: "Billing created",
  INVOICE_RAISED: "Invoice raised",
  INVOICE_UPDATED: "Invoice updated",
  PAYMENT_RECEIVED: "Payment received",
  PAYMENT_REVERTED: "Payment reverted",
  REMARK: "Remark",
  FIELD_CHANGE: "Field change",
};

export default function AdminBilling() {
  const [kpis, setKpis] = useState(null);
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [bucket, setBucket] = useState("all");
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState(null); // record being updated
  const [form, setForm] = useState({});
  const [dupWarning, setDupWarning] = useState(null); // duplicate info from 409
  const [historyOf, setHistoryOf] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      api.get("/billing/kpis"),
      api.get("/billing/queue", { params: { status: bucket, q: q || undefined } }),
      api.get("/billing/company-summary"),
      api.get("/billing/recruiter-summary"),
    ])
      .then(([k, qres, c, r]) => {
        setKpis(k.data);
        setItems(qres.data);
        setCompanies(c.data);
        setRecruiters(r.data);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [bucket, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openEdit = (record) => {
    setForm({
      invoice_raised: !!record.invoice_raised,
      invoice_number: record.invoice_number || "",
      invoice_date: record.invoice_date || "",
      payment_received: !!record.payment_received,
      payment_received_date: record.payment_received_date || "",
      remarks: record.remarks || "",
    });
    setDupWarning(null);
    setEditing(record);
  };

  const submit = async (allowDuplicate = false) => {
    setBusy(true);
    try {
      const payload = { ...form, allow_duplicate: allowDuplicate };
      const res = await api.put(`/billing/${editing.id}`, payload);
      toast.success(`Billing ${res.data.bill_code} updated`);
      setEditing(null);
      setDupWarning(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.can_override) {
        setDupWarning(detail);
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading || !kpis) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 rounded-lg" />
        <div className="grid sm:grid-cols-5 gap-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="billing-back-link">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Link>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mt-2">Billing &amp; Payments</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Tracks manual billing and payment status only. Invoices are prepared outside the system.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="billing-kpis">
        <KpiCard label="Ready for Invoice" value={kpis.ready_for_invoice} icon={ReceiptText} tone="primary" testId="kpi-ready-for-invoice" />
        <KpiCard label="Invoice Pending" value={kpis.invoice_pending} icon={FileText} tone="warning" testId="kpi-invoice-pending" />
        <KpiCard label="Invoice Raised" value={kpis.invoice_raised} icon={CircleCheck} tone="violet" testId="kpi-invoice-raised" />
        <KpiCard label="Payment Pending" value={kpis.payment_pending} icon={Wallet} tone="slate" testId="kpi-payment-pending" />
        <KpiCard label="Payment Received" value={kpis.payment_received} icon={CircleDollarSign} tone="success" testId="kpi-payment-received" />
      </div>

      <Tabs defaultValue="queue">
        <TabsList className="max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="queue" data-testid="billing-tab-queue">Billing Queue</TabsTrigger>
          <TabsTrigger value="company" data-testid="billing-tab-company">Company Billing Summary</TabsTrigger>
          <TabsTrigger value="recruiter" data-testid="billing-tab-recruiter">Recruiter Billing Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="mt-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="billing-search" placeholder="Search billing ID, candidate, company, invoice number..." className="pl-9 bg-white" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <Select value={bucket} onValueChange={setBucket}>
              <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="billing-bucket-filter">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                {BUCKETS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {items.length === 0 ? (
            <EmptyState icon={ReceiptText} title="No billing records"
              description="Records appear automatically when an employment reaches 'Ready for Invoice'." />
          ) : (
            <Card className="rounded-xl border-border shadow-none overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Candidate</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Employment</TableHead>
                      <TableHead>Invoice No.</TableHead>
                      <TableHead>Invoice Date</TableHead>
                      <TableHead>Payment Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((b) => (
                      <TableRow key={b.id} className="hover:bg-muted/60" data-testid="billing-row">
                        <TableCell className="font-mono text-xs">{b.bill_code}</TableCell>
                        <TableCell>
                          <p className="font-medium">{b.candidate?.full_name || "—"}</p>
                          <p className="text-xs text-muted-foreground font-mono">{b.candidate?.code}</p>
                        </TableCell>
                        <TableCell className="text-sm">{b.company?.name || "—"}</TableCell>
                        <TableCell className="text-sm">
                          <Link to={`/admin/employment/${b.employment_id}`} className="font-mono text-xs text-primary hover:underline">{b.employment?.emp_code}</Link>
                        </TableCell>
                        <TableCell className="text-sm font-mono">{b.invoice_number || "—"}</TableCell>
                        <TableCell className="text-sm">{formatDate(b.invoice_date)}</TableCell>
                        <TableCell className="text-sm">{formatDate(b.payment_received_date)}</TableCell>
                        <TableCell><BillingBadge record={b} /></TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white" onClick={() => openEdit(b)} data-testid={`billing-update-${b.id}`}>
                              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Update
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 rounded-lg" onClick={() => setHistoryOf(b)} data-testid={`billing-history-${b.id}`}>
                              <History className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="company" className="mt-4">
          {companies.length === 0 ? (
            <EmptyState icon={Building2} title="No company billing data" description="Summaries appear once billing records exist." />
          ) : (
            <Card className="rounded-xl border-border shadow-none overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company</TableHead>
                      <TableHead className="text-right">Ready for Invoice</TableHead>
                      <TableHead className="text-right">Invoice Raised</TableHead>
                      <TableHead className="text-right">Payment Pending</TableHead>
                      <TableHead className="text-right">Payment Received</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companies.map((c) => (
                      <TableRow key={c.company_id || "none"} data-testid="company-summary-row">
                        <TableCell>
                          <p className="font-medium">{c.company_name}</p>
                          <p className="text-xs text-muted-foreground font-mono">{c.company_code}</p>
                        </TableCell>
                        <TableCell className="text-right font-medium">{c.ready_for_invoice}</TableCell>
                        <TableCell className="text-right">{c.invoice_raised}</TableCell>
                        <TableCell className="text-right">{c.payment_pending}</TableCell>
                        <TableCell className="text-right">{c.payment_received}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="recruiter" className="mt-4">
          {recruiters.length === 0 ? (
            <EmptyState icon={UserRound} title="No recruiter billing data" description="Summaries appear once billing records exist." />
          ) : (
            <Card className="rounded-xl border-border shadow-none overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Recruiter</TableHead>
                      <TableHead className="text-right">Candidates Ready</TableHead>
                      <TableHead className="text-right">Invoice Raised</TableHead>
                      <TableHead className="text-right">Payment Received</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recruiters.map((r) => (
                      <TableRow key={r.recruiter_id || "none"} data-testid="recruiter-summary-row">
                        <TableCell className="font-medium">{r.recruiter_name}</TableCell>
                        <TableCell className="text-right font-medium">{r.candidates_ready}</TableCell>
                        <TableCell className="text-right">{r.invoice_raised}</TableCell>
                        <TableCell className="text-right">{r.payment_received}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Update dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setDupWarning(null); } }}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[90vh] overflow-y-auto" data-testid="billing-update-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Update billing — {editing?.bill_code}</DialogTitle>
            <DialogDescription>
              {editing?.candidate?.full_name} · {editing?.company?.name} · {editing?.employment?.emp_code}. Every change is recorded permanently.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <label className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 cursor-pointer">
              <span className="text-sm font-medium">Invoice raised</span>
              <Switch checked={!!form.invoice_raised} onCheckedChange={(v) => setForm({ ...form, invoice_raised: v })} data-testid="billing-invoice-raised-switch" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Invoice number</Label>
                <Input data-testid="billing-invoice-number-input" placeholder="Manual entry" value={form.invoice_number || ""} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Invoice date</Label>
                <Input type="date" data-testid="billing-invoice-date-input" value={form.invoice_date || ""} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 cursor-pointer">
              <span className="text-sm font-medium">Payment received</span>
              <Switch checked={!!form.payment_received} onCheckedChange={(v) => setForm({ ...form, payment_received: v })} data-testid="billing-payment-received-switch" />
            </label>
            <div className="space-y-1.5">
              <Label>Payment received date</Label>
              <Input type="date" data-testid="billing-payment-date-input" value={form.payment_received_date || ""} onChange={(e) => setForm({ ...form, payment_received_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Remarks</Label>
              <Textarea data-testid="billing-remarks-input" rows={2} value={form.remarks || ""} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Optional context..." />
            </div>

            {dupWarning && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="billing-duplicate-warning">
                <p className="text-sm text-amber-900 flex items-start gap-2">
                  <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0" />
                  {dupWarning.message} (Duplicate of {dupWarning.duplicate_of})
                </p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-lg bg-white" onClick={() => setDupWarning(null)} data-testid="billing-duplicate-cancel">
                    Cancel &amp; correct
                  </Button>
                  <Button size="sm" className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white" onClick={() => submit(true)} disabled={busy} data-testid="billing-duplicate-continue">
                    Continue Anyway
                  </Button>
                </div>
              </div>
            )}

            {!dupWarning && (
              <Button onClick={() => submit(false)} disabled={busy} className="w-full rounded-lg" data-testid="billing-update-submit">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save changes
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyOf} onOpenChange={(v) => !v && setHistoryOf(null)}>
        <DialogContent className="sm:max-w-md rounded-xl max-h-[85vh] overflow-y-auto" data-testid="billing-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Audit history — {historyOf?.bill_code}</DialogTitle>
            <DialogDescription>Immutable log — never edited or deleted.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            {[...(historyOf?.history || [])].reverse().map((e) => (
              <div key={e.id} className="rounded-lg border border-border p-3" data-testid="billing-history-item">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="flex items-center gap-2">
                    <span className="inline-flex rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                      {EVENT_LABELS[e.event_type] || e.event_type}
                    </span>
                    {e.override && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide" data-testid="billing-override-badge">
                        <TriangleAlert className="h-3 w-3" /> Override
                      </span>
                    )}
                  </span>
                  <span className="text-[11px] text-muted-foreground font-mono">{formatDateTime(e.at)}</span>
                </div>
                {(e.old !== null || e.new !== null) && (e.old !== undefined || e.new !== undefined) && (
                  <p className="text-sm mt-1.5">
                    {e.field && <span className="text-muted-foreground">{(e.field || "").replaceAll("_", " ")}: </span>}
                    {String(e.old ?? "—")}<span className="text-muted-foreground mx-1.5">→</span>
                    <span className="font-medium">{String(e.new ?? "—")}</span>
                  </p>
                )}
                {e.remarks && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{e.remarks}</p>}
                <p className="text-[11px] text-muted-foreground mt-1.5">by <span className="font-medium text-foreground">{e.actor_name}</span></p>
              </div>
            ))}
            {(historyOf?.history || []).length === 0 && <p className="text-sm text-muted-foreground">No history yet.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
